<template>
    <div class="time-picker-wrapper">
        <vue-multiselect
            v-model="selectedTime"
            :options="timeOptions"
            :placeholder="placeholder"
            label="label"
            track-by="id"
            :show-labels="false"
            :allow-empty="false"
            :searchable="false"
            @select="onTimeSelect"
            @open="scrollToSelected"
            class="custom-time-picker"
        >
            <!-- Custom single label to include the clock icon -->
            <template #singleLabel="{ option }">
                <div class="time-label-content">
                    <i class="ri-time-line clock-icon"></i>
                    <span class="selected-time-text">{{ option.label }}</span>
                </div>
            </template>

            <!-- Custom placeholder to include the clock icon -->
            <template #placeholder>
                <div class="time-label-content text-muted">
                    <i class="ri-time-line clock-icon"></i>
                    <span class="placeholder-text">{{ placeholder }}</span>
                </div>
            </template>

            <!-- Custom option layout -->
            <template #option="{ option }">
                <div class="time-option-item">
                    {{ option.label }}
                </div>
            </template>
        </vue-multiselect>
    </div>
</template>

<script>
/**
 * TimePicker Component
 * Features:
 * - 15-minute intervals
 * - Display format: hh:mm AM/PM (e.g., 07:15 AM)
 * - Value format: { hours: 23, minutes: 17 }
 */
export default {
    name: 'TimePicker',
    props: {
        modelValue: {
            type: [Object, String],
            default: () => ({ hours: 7, minutes: 0 })
        },
        placeholder: {
            type: String,
            default: 'Select Time'
        }
    },
    emits: ['update:modelValue'],
    data() {
        return {
            selectedTime: null,
            timeOptions: []
        };
    },
    created() {
        this.generateTimeOptions();
        this.initSelectedTime();
    },
    watch: {
        modelValue: {
            handler(newVal) {
                this.updateInternalValue(newVal);
            },
            deep: true
        }
    },
    methods: {
        /**
         * Generates time options for every 15 minutes
         */
        generateTimeOptions() {
            const options = [];
            for (let hour = 0; hour < 24; hour++) {
                for (let minute = 0; minute < 60; minute += 15) {
                    const ampm = hour >= 12 ? 'PM' : 'AM';
                    let displayHour = hour % 12;
                    displayHour = displayHour === 0 ? 12 : displayHour;
                    
                    const mm = minute.toString().padStart(2, '0');
                    const displayHH = displayHour.toString().padStart(2, '0');
                    const label = `${displayHH}:${mm} ${ampm}`;

                    // Internal tracking ID
                    const id = `${hour.toString().padStart(2, '0')}:${mm}:00`;

                    options.push({
                        id,
                        value: {
                            hours: hour,
                            minutes: minute
                        },
                        label: label
                    });
                }
            }
            this.timeOptions = options;
        },

        /**
         * Initializes the selected time based on modelValue
         */
        initSelectedTime() {
            if (this.modelValue) {
                this.updateInternalValue(this.modelValue);
            }
        },

        /**
         * Updates the internal selectedTime object based on the object value
         */
        updateInternalValue(value) {
            if (!value) {
                this.selectedTime = null;
                return;
            }

            // Handle both object and string (for backward compatibility if needed)
            if (typeof value === 'object') {
                this.selectedTime = this.timeOptions.find(opt => 
                    parseInt(opt.value.hours) === parseInt(value.hours) && 
                    parseInt(opt.value.minutes) === parseInt(value.minutes)
                ) || null;
            } else if (typeof value === 'string') {
                this.selectedTime = this.timeOptions.find(opt => opt.id === value) || null;
            }
        },

        /**
         * Handles the selection event
         */
        onTimeSelect(option) {
            this.$emit('update:modelValue', option.value);
        },

        /**
         * Scroll to the selected option when dropdown opens
         */
        scrollToSelected() {
            setTimeout(() => {
                const selectedOption = this.$el.querySelector('.multiselect__option--selected');
                if (selectedOption) {
                    selectedOption.scrollIntoView({ block: 'center', behavior: 'smooth' });
                }
            }, 0);
        }
    }
};
</script>

<style scoped>
.time-picker-wrapper {
    position: relative;
    width: 100%;
}

.custom-time-picker :deep(.multiselect__placeholder) {
    margin-bottom: 0;
    padding-top: 0;
}

.time-label-content {
    color: #959595 !important;
    font-size: 0.95rem !important;
    display: flex;
    align-items: center;
    gap: 8px;
}
.selected-time-text {
    color: #3e4045 !important;
    font-size: 0.95rem !important;
}

.clock-icon {
    font-size: 1.2rem;
    display: flex;
    align-items: center;
}

/* Dropdown list styling */
.custom-time-picker :deep(.multiselect__content) {
    max-height: 240px;
}
.custom-time-picker :deep(.multiselect__select) {
    display: none;
}
</style>
